import { Link } from "@tanstack/react-router";
import { useRef } from "react";

import { WithcoWordmark } from "@/web/assets";
import { env } from "@/web/lib/env";
import { cn } from "@/web/lib/styling";
import { WithUsCta } from "@/web/routes/-marketing/WithUsCta";
import { FeaturedOfferings } from "@/web/routes/-home/FeaturedOfferings";
import { PathCards } from "@/web/routes/-home/PathCards";
import { TransitionStory } from "@/web/routes/-home/TransitionStory";
import { useTransitionStory, useTransitionStoryRefs } from "@/web/routes/-home/useTransitionStory";

import "@/web/routes/-home/home.css";

const FACADE_FILENAME = "homepages/facade-photo.jpg";

/** The scroll story's storefront photo; the traced line art is drawn to match it. */
const FACADE_PHOTO = `${env.VITE_WITHCO_IMAGE_CDN_BASE}/${FACADE_FILENAME}`;

/** Facade cropped to the 1200x630 Open Graph card via an ImageKit path transform. */
export const HOME_OG_IMAGE = `${env.VITE_WITHCO_IMAGE_CDN_BASE}/tr:w-1200,h-630,c-maintain_ratio,fo-auto/${FACADE_FILENAME}`;
export const HOME_OG_IMAGE_ALT = "A row of brick storefronts on a small-town Main Street";

/**
 * `/` — ported from the prototype's `hybrid.html`. Every top-level section
 * declares a `data-nav-theme` for the fixed `MarketingNavbar`.
 *
 * `--gutter` is the prototype's page gutter; `--nav-h` is the fixed navbar's
 * height (h-16 / md:h-20), which the sticky story sits beneath.
 */
export function HomePage() {
  const storyRefs = useTransitionStoryRefs();
  const railAnchor = useRef<HTMLDivElement>(null);
  const { setHandoffTarget, onRailScroll, jumpToStage } = useTransitionStory(storyRefs, railAnchor);

  return (
    <main
      id="main"
      className={cn(
        // Optical sizing off: the prototype uses static DM Sans; the variable font's
        // opsz axis would narrow the large hero type.
        "home-v2 bg-white font-sans text-[#050f23] [font-optical-sizing:none]",
        "[--gutter:clamp(24px,4.5vw,76px)] [--nav-h:64px] max-[760px]:[--gutter:24px] md:[--nav-h:80px]",
      )}
    >
      <HomeHero onExplore={() => jumpToStage(0)} />
      <TransitionStory refs={storyRefs} photoUrl={FACADE_PHOTO} onJump={jumpToStage} />
      <FeaturedOfferings
        railAnchor={railAnchor}
        handoffTargetRef={setHandoffTarget}
        onRailScroll={onRailScroll}
      />
      <PathCards />
      <HomeFooterBand />
    </main>
  );
}

function HomeHero({ onExplore }: { onExplore: () => void }) {
  return (
    <section
      data-nav-theme="white"
      // The top padding clears the fixed MarketingNavbar (the prototype's 110px
      // header + its 48px hero padding; 122px + 38px on mobile).
      className="px-(--gutter) pt-[158px] pb-[50px] max-[760px]:pt-[160px]"
    >
      <h1
        className={cn(
          "mt-[30px] max-w-[1300px] font-sans text-[clamp(64px,8.2vw,132px)] leading-[0.99] font-normal tracking-[-0.055em] min-[1800px]:max-w-[1400px]",
          "max-[760px]:mt-[23px] max-[760px]:text-[clamp(45px,9.2vw,70px)] max-[760px]:leading-[1.04]",
        )}
      >
        The places we love.
        <br />A future we own.
      </h1>
      <div
        className={cn(
          "mt-[26px] grid grid-cols-[minmax(0,1fr)_auto] items-end gap-[clamp(28px,5vw,80px)]",
          "max-[760px]:flex max-[760px]:flex-col-reverse max-[760px]:items-start max-[700px]:flex-col",
        )}
      >
        <p className="w-full max-w-[800px] justify-self-start text-left text-[clamp(18px,1.7vw,26px)] leading-[1.4] max-[760px]:text-[19px]">
          We transition the places a neighborhood depends on into community ownership by backing the
          local businesses that make them valuable.
        </p>
        {/* Lands on "01 / A building" rather than the top of the story (the
            photo); the href is the no-JS fallback. */}
        <a
          href="#transition"
          onClick={(e) => {
            e.preventDefault();
            onExplore();
          }}
          className="flex w-max items-center justify-center gap-[6px] justify-self-end border-b-2 border-current pb-[8px] font-[monospace] text-[12px] text-[#34308b] uppercase"
        >
          Explore how it works <span aria-hidden="true">↓</span>
        </a>
      </div>
    </section>
  );
}

/**
 * The prototype's footer band, above the shared legal `MarketingFooter`. Its
 * side padding matches the footer's (px-4 / md:px-8) so the logo lines up with
 * the copyright and the CTA with the footer links.
 */
function HomeFooterBand() {
  return (
    <div
      data-nav-theme="white"
      className="flex items-center justify-between gap-[25px] border-t border-[#34308b33] px-4 py-[45px] text-[14px] text-[#34308b] md:px-8"
    >
      <Link to="/" aria-label="Home" className="block w-[145px] shrink-0 max-[700px]:w-[115px]">
        <WithcoWordmark aria-hidden className="block h-auto w-full" />
      </Link>
      <WithUsCta className="max-[700px]:px-[10px] max-[700px]:py-[11px] max-[700px]:text-[12px]" />
    </div>
  );
}
